import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { 
  Search, Package, DollarSign, ShoppingCart, ChevronRight
} from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SupplementsViewPage = () => {
  const { t } = useLanguage();
  const { user } = useAuth();
  const navigate = useNavigate();
  const isClient = user?.user_type === 'client';
  const [supplements, setSupplements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  const fetchSupplements = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/supplements`);
      setSupplements(response.data);
    } catch (error) {
      console.error('Error fetching supplements:', error);
      toast.error('Error al cargar suplementos');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSupplements();
  }, [fetchSupplements]);

  const getStockBadge = (stock) => {
    if (stock === 0) {
      return <Badge className="bg-red-100 text-red-700 border-red-200">{t('outOfStock')}</Badge>;
    }
    if (stock <= 10) {
      return <Badge className="bg-amber-100 text-amber-700 border-amber-200">{t('lowStock')}</Badge>;
    }
    return <Badge className="bg-green-100 text-green-700 border-green-200">{t('inStock')}</Badge>;
  };

  const filteredSupplements = supplements.filter(s =>
    s.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (s.category && s.category.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className={`animate-spin rounded-full h-12 w-12 border-4 ${isClient ? 'border-orange-100 border-t-orange-500' : 'border-[#467322] border-t-transparent'}`} />
      </div>
    );
  }

  // Client layout
  if (isClient) {
    return (
      <div className="max-w-2xl mx-auto space-y-5 animate-fade-in" data-testid="supplements-view-page">
        {/* Header */}
        <div className="flex items-center justify-between pt-1">
          <div>
            <h1 className="text-3xl font-bold text-gray-900" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
              Tienda
            </h1>
            <p className="text-sm text-gray-400 mt-1 font-medium">Catálogo de productos</p>
          </div>
          <button
            onClick={() => navigate('/client/orders')}
            className="flex items-center gap-2 bg-gradient-to-r from-pink-500 to-rose-500 text-white px-4 py-2.5 rounded-full text-sm font-bold shadow-lg shadow-pink-500/25 hover:shadow-pink-500/40 hover:-translate-y-0.5 transition-all duration-300"
            data-testid="go-to-cart-btn"
          >
            <ShoppingCart size={16} />
            Mi Carrito
          </button>
        </div>

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
          <Input
            placeholder="Buscar producto..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-11 bg-white border-gray-100 focus:border-orange-400 focus:ring-2 focus:ring-orange-500/20 rounded-xl h-11 shadow-sm"
            data-testid="search-supplements"
          />
        </div>

        {/* Products */}
        {filteredSupplements.length === 0 ? (
          <div className="bg-white rounded-3xl border border-gray-100/50 shadow-sm p-10 text-center">
            <div className="w-20 h-20 bg-gradient-to-br from-orange-50 to-amber-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Package size={32} className="text-orange-300" />
            </div>
            <p className="font-bold text-gray-700 text-lg" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>Sin productos</p>
            <p className="text-sm text-gray-400 mt-1">El catálogo estará disponible pronto</p>
          </div>
        ) : (
          <div className="space-y-3">
            {filteredSupplements.map((supplement, idx) => (
              <div 
                key={supplement.id}
                className="bg-white rounded-2xl border border-gray-100/50 overflow-hidden shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-300"
                style={{ animationDelay: `${idx * 60}ms` }}
              >
                <div className="h-1 bg-gradient-to-r from-orange-400 to-amber-500" />
                <div className="p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <p className="font-bold text-gray-900 text-sm">{supplement.name}</p>
                      {supplement.category && (
                        <p className="text-xs text-orange-500 font-medium mt-0.5">{supplement.category}</p>
                      )}
                      {supplement.description && (
                        <p className="text-xs text-gray-400 mt-1.5 line-clamp-2">{supplement.description}</p>
                      )}
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className="text-xl font-bold text-gray-900" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>${supplement.price.toFixed(2)}</p>
                      {getStockBadge(supplement.stock)}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Floating Cart Access */}
        <button
          onClick={() => navigate('/client/orders')}
          className="bg-white rounded-2xl border border-gray-100/50 p-4 w-full text-left shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-300 flex items-center gap-4 group"
          data-testid="quick-access-carrito"
        >
          <div className="w-10 h-10 bg-gradient-to-br from-pink-50 to-rose-50 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
            <ShoppingCart size={20} className="text-pink-500" />
          </div>
          <div className="flex-1">
            <p className="font-bold text-gray-900 text-sm" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>Mi Carrito / Pedidos</p>
            <p className="text-[11px] text-gray-400 mt-0.5">Ver mis pedidos realizados</p>
          </div>
          <ChevronRight size={16} className="text-gray-300" />
        </button>
      </div>
    );
  }

  // Admin/Distributor layout (unchanged)
  return (
    <div className="space-y-6 animate-fade-in" data-testid="supplements-view-page">
      <div>
        <h1 className="text-4xl font-bold text-[#467322]" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
          {t('supplements')}
        </h1>
        <p className="text-gray-600 mt-1">Catálogo de productos disponibles</p>
      </div>

      <Card className="bg-white border border-[#467322]/20">
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[#467322]" size={20} />
            <Input
              placeholder={`${t('search')}...`}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 border-[#467322]/30 focus:border-[#467322]"
              data-testid="search-supplements"
            />
          </div>
        </CardContent>
      </Card>

      {filteredSupplements.length === 0 ? (
        <Card className="bg-white border border-[#467322]/20">
          <CardContent className="py-12">
            <div className="text-center text-gray-500">
              <Package size={48} className="mx-auto mb-4 opacity-50" />
              <p>{t('noSupplements')}</p>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredSupplements.map((supplement) => (
            <Card 
              key={supplement.id}
              className="bg-white border border-[#467322]/20 hover:shadow-lg transition-all duration-200"
            >
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between">
                  <div>
                    <CardTitle className="text-lg text-black">{supplement.name}</CardTitle>
                    {supplement.category && (
                      <p className="text-sm text-[#467322]">{supplement.category}</p>
                    )}
                  </div>
                  {getStockBadge(supplement.stock)}
                </div>
              </CardHeader>
              <CardContent>
                {supplement.description && (
                  <p className="text-sm text-gray-600 mb-4 line-clamp-2">{supplement.description}</p>
                )}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-1 text-[#467322]">
                    <DollarSign size={20} />
                    <span className="text-2xl font-bold">{supplement.price.toFixed(2)}</span>
                  </div>
                  <div className="text-sm text-gray-500">
                    Stock: {supplement.stock}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default SupplementsViewPage;
